import ArrowForward from "@mui/icons-material/ArrowForward";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RedemptionAddressAutocomplete from "@/components/redemption/RedemptionAddressAutocomplete";
import RedemptionPhoneField from "@/components/redemption/RedemptionPhoneField";
import { GfgButton } from "@/components/ui/GfgButton";
import { GfgSelect } from "@/components/ui/GfgSelect";
import { GfgTextField } from "@/components/ui/GfgTextField";
import type { AddressParts, RecipientFormDraft, RegionOption } from "@/interfaces/redemption";

interface RedemptionDetailsFormProps {
  form: RecipientFormDraft;
  errors: Set<string>;
  setField: (key: keyof RecipientFormDraft, value: string) => void;
  /** Fill street/city/state/zip together from a chosen Google suggestion. */
  onAddressSelect: (parts: AddressParts) => void;
  /** Gift-type branch — physical ships; charity/experience collect email only. */
  needsShipping: boolean;
  isCharity: boolean;
  isExperience: boolean;
  /** US uses Google autocomplete for the street field; other countries a plain input. */
  isUS: boolean;
  /** Destination country (fixed) + its address-format profile. */
  shippingCountry: string;
  countryName: string;
  countryFlag: string;
  regionLabel: string | null;
  regionOptions: RegionOption[];
  postalLabel: string | null;
  /** Redeem POST failed — shown inline above the CTA. */
  submitFailed: boolean;
  /** Primary CTA label + action (advance to address-verify / review). */
  ctaLabel: string;
  onPrimary: () => void;
}

// Prototype CTA sizing (`.iv-continue`): 15×40 padding, 12px label, 8px radius.
const CTA_SIZE = "rounded-lg px-10 py-3.75 text-[12px]";

/**
 * The recipient-details form fields (contact + shipping) — the `form` phase of
 * `RedemptionDetails`, split out to keep that file orchestration-only. Purely
 * presentational: the parent owns validation, submission, and the address-format
 * derivation passed in as props.
 */
export default function RedemptionDetailsForm({
  form,
  errors,
  setField,
  onAddressSelect,
  needsShipping,
  isCharity,
  isExperience,
  isUS,
  shippingCountry,
  countryName,
  countryFlag,
  regionLabel,
  regionOptions,
  postalLabel,
  submitFailed,
  ctaLabel,
  onPrimary,
}: RedemptionDetailsFormProps) {
  const sectionTitle = needsShipping
    ? "Where can we send your gift?"
    : isCharity
      ? "Where can we send your charity confirmation?"
      : "Where can we send your Experience gift voucher?";
  const formLead = isCharity
    ? "You'll receive an email confirming your charity gift selection, along with more information about the cause you supported."
    : isExperience
      ? "You will receive a gift voucher via email that you can use to redeem this gift."
      : "";

  return (
    <div>
      <h2 className="mb-5.5 font-crimson text-[22px] font-normal text-rodeo-black sm:text-[25px]">
        {sectionTitle}
      </h2>

      <div className="mb-4 flex gap-4">
        <GfgTextField
          id="iv-first"
          className="min-w-0 flex-1"
          label="First name"
          autoComplete="given-name"
          ignorePasswordManagers
          value={form.firstName}
          error={errors.has("firstName")}
          errorText="Please enter your first name."
          onChange={(e) => setField("firstName", e.target.value)}
        />
        <GfgTextField
          className="min-w-0 flex-1"
          label="Last name"
          autoComplete="family-name"
          ignorePasswordManagers
          value={form.lastName}
          error={errors.has("lastName")}
          errorText="Please enter your last name."
          onChange={(e) => setField("lastName", e.target.value)}
        />
      </div>

      {needsShipping && (
        <>
          {/* Country is fixed to the collection's shipping country — the recipient only
              saw gifts that ship there. Read-only by design. */}
          <div className="relative mb-4 flex items-center gap-2 rounded-lg border border-smoke bg-clear-dust p-3.5">
            <span className="-translate-y-1/2 absolute top-0 left-2.75 bg-clear-dust px-1.25 font-lato text-[10px] font-bold uppercase tracking-[1.5px] text-dust-ink">
              Country
            </span>
            <span className="flex min-w-0 flex-1 items-center gap-2 font-lato text-[14px] text-rodeo-black">
              <span aria-hidden="true">{countryFlag}</span>
              {countryName}
            </span>
            <LockOutlinedIcon className="shrink-0 text-dark-dust" sx={{ fontSize: 15 }} />
          </div>

          <div className="flex flex-col gap-4">
            {isUS ? (
              <RedemptionAddressAutocomplete
                value={form.address}
                error={errors.has("address")}
                errorText="Please enter your street address."
                onChange={(street) => setField("address", street)}
                onSelect={onAddressSelect}
              />
            ) : (
              <GfgTextField
                id="iv-address"
                label="Address"
                autoComplete="address-line1"
                ignorePasswordManagers
                value={form.address}
                error={errors.has("address")}
                errorText="Please enter your street address."
                onChange={(e) => setField("address", e.target.value)}
              />
            )}
            <GfgTextField
              label="Apartment / suite"
              hint="— optional"
              autoComplete="address-line2"
              ignorePasswordManagers
              value={form.apartment}
              onChange={(e) => setField("apartment", e.target.value)}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-4">
            <GfgTextField
              className="min-w-0 grow basis-full sm:basis-0"
              label="City"
              autoComplete="address-level2"
              ignorePasswordManagers
              value={form.city}
              error={errors.has("city")}
              errorText="Please enter your city."
              onChange={(e) => setField("city", e.target.value)}
            />
            {regionLabel &&
              (regionOptions.length > 0 ? (
                <GfgSelect
                  className="min-w-0 grow basis-full sm:basis-0"
                  label={regionLabel}
                  options={[{ value: "", label: "" }, ...regionOptions]}
                  autoComplete="address-level1"
                  ignorePasswordManagers
                  value={form.state}
                  error={errors.has("state")}
                  errorText={`Please select your ${regionLabel.toLowerCase()}.`}
                  onChange={(e) => setField("state", e.target.value)}
                />
              ) : (
                <GfgTextField
                  className="min-w-0 grow basis-full sm:basis-0"
                  label={regionLabel}
                  autoComplete="address-level1"
                  ignorePasswordManagers
                  value={form.state}
                  error={errors.has("state")}
                  errorText={`Please enter your ${regionLabel.toLowerCase()}.`}
                  onChange={(e) => setField("state", e.target.value)}
                />
              ))}
            {postalLabel && (
              <GfgTextField
                className="min-w-0 basis-full sm:basis-[30%]"
                label={postalLabel}
                autoComplete="postal-code"
                ignorePasswordManagers
                value={form.zipCode}
                error={errors.has("zipCode")}
                errorText={`Please enter your ${postalLabel.toLowerCase()}.`}
                onChange={(e) => setField("zipCode", e.target.value)}
              />
            )}
          </div>

          <div className="mt-4">
            <RedemptionPhoneField
              value={form.phone}
              error={errors.has("phone")}
              errorText="Please enter a valid phone number."
              initialCountry={shippingCountry}
              onChange={(formatted, dial, iso) => {
                setField("phone", formatted);
                setField("phoneDial", dial);
                setField("phoneCountry", iso);
              }}
            />
          </div>
        </>
      )}

      <div className="mt-4">
        <GfgTextField
          id="iv-email"
          type="email"
          label="Email address"
          autoComplete="email"
          hint={
            isCharity
              ? "— for charity confirmation"
              : isExperience
                ? "— for your voucher"
                : "— for order updates"
          }
          ignorePasswordManagers
          value={form.email}
          error={errors.has("email")}
          errorText="Please enter a valid email address."
          onChange={(e) => setField("email", e.target.value)}
        />
      </div>

      {formLead && (
        <p className="mt-5.5 max-w-115 font-lato text-[15px] leading-[1.6] text-graphite">
          {formLead}
        </p>
      )}
      {submitFailed && (
        <p className="mt-4 font-lato text-[13px] text-good-wine">
          Something went wrong sending your gift. Please try again.
        </p>
      )}

      {/* Desktop CTA; phone uses the sticky shelf pill (hidden here). Always "Continue" —
          every gift type advances to the review. */}
      <GfgButton
        variant="primary"
        className={`mt-3.5 hidden sm:inline-flex ${CTA_SIZE}`}
        endIcon={<ArrowForward sx={{ fontSize: 18 }} />}
        onClick={onPrimary}
      >
        {ctaLabel}
      </GfgButton>
    </div>
  );
}
